import React, { Suspense, useEffect, useRef, useState } from "react";
import {
  alpha,
  Box,
  Container,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseCircle from "iconsax-reactjs/dist/esm/CloseCircle.js";
import HamburgerMenu from "iconsax-reactjs/dist/esm/HamburgerMenu.js";
import Logo from "./Logo";
import NavGlow from "./NavGlow";
import NavLinks from "./NavLinks";
import NavActions from "./NavActions";
import BottomNav from "./BottomNav";
import { navLinks } from "./NavLinks.data";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

const MobileDrawer = React.lazy(() => import("./MobileDrawer"));

// Visual height the navbar reserves (top gap + bar height + a little
// breathing room). Keep in sync with the `top: 14` / `height: 72` below.
// MainLayout imports this to space the page content correctly, since the
// navbar is `position: fixed` and no longer takes up flow space itself.
export const NAVBAR_TOTAL_OFFSET = 102;

const SHOW_AT_TOP = 80; // never hide the navbar near the very top of the page
const SCROLL_DELTA_THRESHOLD = 6; // ignore tiny jitter (bounce scroll, trackpads)
const STOP_DELAY = 300; // ms of no scrolling before the navbar reappears

export default function Navbar({ isAuthenticated }) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user, token } = useSelector((state) => state.userSlice);
  const [scrolled, setScrolled] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hidden, setHidden] = useState(false);

  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const stopTimer = useRef(null);
  useEffect(() => {
    const activeLink = navLinks.find((link) => link.href === pathname);

    setActiveId(activeLink?.id ?? null);
  }, [pathname]);
  useEffect(() => {
    lastScrollY.current = window.scrollY;

    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;

      requestAnimationFrame(() => {
        const currentY = window.scrollY;
        setScrolled(currentY > 12);

        const delta = currentY - lastScrollY.current;

        if (currentY < SHOW_AT_TOP) {
          setHidden(false);
        } else if (Math.abs(delta) > SCROLL_DELTA_THRESHOLD) {
          setHidden(delta > 0); // scrolling down => hide, scrolling up => show
        }
        lastScrollY.current = currentY;

        ticking.current = false;
      });

      // Once scrolling stops for a moment, bring the navbar back regardless
      // of direction — this is the "friendly" part users expect.
      if (stopTimer.current) clearTimeout(stopTimer.current);
      stopTimer.current = setTimeout(() => setHidden(false), STOP_DELAY);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (stopTimer.current) clearTimeout(stopTimer.current);
    };
  }, []);

  // Never hide the navbar while the mobile drawer is open, or the user
  // would be stuck unable to reach the close button.
  const isHidden = hidden && !drawerOpen;

  return (
    <>
      <Box
        sx={{
          position: "sticky",
          direction:"ltr",
          top: 14,
          left: 0,
          right: 0,
          zIndex: 101,
          px: 2,
          display: "flex",
          justifyContent: "center",
          pointerEvents: isHidden ? "none" : "auto",
          transform: isHidden ? "translateY(-140px)" : "translateY(0)",
          opacity: isHidden ? 0 : 1,
          transition: "transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.35s ease",
        }}
      >
        <Container
          disableGutters
          maxWidth="lg"
          sx={{
            position: "relative",
            height: 72,
            borderRadius: 3,
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            bgcolor: scrolled
              ? "rgba(255,255,255,0.92)"
              : "rgba(255,255,255,0.78)",
                      // bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            
            border: "1px solid rgba(255,255,255,0.9)",
            boxShadow: scrolled
              ? "0 1px 0 rgba(255,255,255,.8) inset, 0 22px 44px -20px rgba(30,58,138,.34), 0 10px 20px -10px rgba(255,107,53,.18)"
              : "0 1px 0 rgba(255,255,255,.7) inset, 0 18px 40px -20px rgba(30,58,138,.28), 0 8px 18px -10px rgba(255,107,53,.14)",
            transition: "background .3s ease, box-shadow .3s ease",
            overflow: "hidden",
          }}
        >
          <NavGlow />

          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              height: "100%",
              display: "flex",
              alignItems: "center",
              flexDirection: "row-reverse",
              justifyContent: "space-between",
              px: { xs: 2, md: 3 },
            }}
          >
            <Logo
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            />

            <NavLinks activeId={activeId} onSelect={setActiveId} />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ display: { xs: "none", md: "flex" } }}>
                <NavActions
                  isAuthenticated={Boolean(token)}
                  fullname={user?.fullname || "کاربر مهمان"}
                  profileImage={user?.profileImg || null}
                />
              </Box>

              <IconButton
                onClick={() => setDrawerOpen((v) => !v)}
                aria-label={drawerOpen ? "بستن منو" : "منوی اصلی"}
                sx={{
                  display: { xs: "flex", md: "none" },
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "#fff",
                  width: 40,
                  height: 40,
                }}
              >
                {drawerOpen ? (
                  <CloseCircle size={18} />
                ) : (
                  <HamburgerMenu size={18} />
                )}
              </IconButton>
            </Box>
          </Box>
        </Container>
      </Box>

      {drawerOpen && (
        <Suspense fallback={null}>
          <MobileDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            onSelect={setActiveId}
            isAuthenticated={Boolean(token)}
          />
        </Suspense>
      )}

      {isMobile && <BottomNav isAuthenticated={Boolean(token)} />}
    </>
  );
}
